import { BaseService } from '../../../http/BaseHttpService';
import {
    ChatSessionLoadResult,
    ChatSessionsLoadResult,
    DeleteChatSessionsRequest,
    IChatSessionsService,
} from '../../../chat/interfaces/IChatSessionsService';
import { NexusConversationDto } from '../dtos/responses/NexusConversationDto';

import { mapNexusConversationDtoToModel } from '../mappers/NexusConversationMapper';
import { buildNexusUrl } from '../../../../features/utils/buildNexusUrl';
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';

export class NexusConversationsService extends BaseService implements IChatSessionsService {
    public readonly providerType = ChatProviderType.Nexus;
    private readonly subscriptionKey = process.env.REACT_APP_NEXUS_APIM_SUBSCRIPTION_KEY ?? '';
    private readonly subKeyHeader = process.env.REACT_APP_NEXUS_APIM_KEY_HEADER_NAME ?? '';

    constructor(private readonly baseUrl: string) {
        super();
    }

    public async getAllChatsAsync(accessToken: string): Promise<ChatSessionsLoadResult> {
        const response = await this.getResponseAsync<NexusConversationDto[]>(
            {
                url: buildNexusUrl(this.baseUrl, 'conversations'),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        const mappedConversations = response
            .filter((conversation) => !conversation.metadata?.isEphemeral)
            .map((conversation) => mapNexusConversationDtoToModel(conversation));

        // populate both sessions and documentsBySessionId b/c both are returned
        return {
            sessions: mappedConversations.map((conversation) => conversation.session),
            documentsBySessionId: mappedConversations.reduce<Record<string, ChatSessionLoadResult['documents']>>(
                (accumulator, conversation) => {
                    accumulator[conversation.session.id] = conversation.documents;
                    return accumulator;
                },
                {},
            ),
        };
    }

    public async getChatAsync(chatId: string, accessToken: string): Promise<ChatSessionLoadResult> {
        const response = await this.getResponseAsync<NexusConversationDto>(
            {
                url: buildNexusUrl(this.baseUrl, `conversations/${chatId}`),
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );

        return mapNexusConversationDtoToModel(response);
    }

    public async deleteChatAsync(request: DeleteChatSessionsRequest, accessToken: string): Promise<void> {
        if (!request.chatId) {
            return;
        }

        await this.getResponseAsync(
            {
                url: buildNexusUrl(this.baseUrl, `conversations/${request.chatId}`),
                method: 'DELETE',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );
    }

    public async hardDeleteChatAsync(request: DeleteChatSessionsRequest, accessToken: string): Promise<void> {
        const chatId = request.chatId;
        if (!chatId) {
            return;
        }
        await this.getResponseAsync(
            {
                url: buildNexusUrl(this.baseUrl, `conversations/${chatId}/hard`),
                method: 'DELETE',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );
    }
}
